import { Divider } from 'antd';
import { useLocation } from 'react-router-dom';
import React, { useEffect, useMemo } from 'react';
import NotificationsIcon from '@mui/icons-material/Notifications';

import { Sidepanel } from '../Sidepanel';
import { AdminNotification } from '@types';
import { useSettingsContext } from '@providers';
import { adminHttp, NotificationEntityTypes } from '@network';
import { ChatsList } from '@components/Chat/chats-list';
import { NotificationsItem } from './notifications-item';
import cls from './notifications.module.css';

export type NotificationType = Record<NotificationEntityTypes, AdminNotification[]>;

export const Notifications = () => {
  const sidepanelName = 'Notifications';
  const location = useLocation();
  const {
    panelName,
    setPanelName,
    notificationsMap,
    refreshNotifications,
    mentioned,
  } = useSettingsContext();

  useEffect(() => {
    setPanelName(null);
  }, [location.pathname]);

  const togglePanel = () => {
    const nextPanelName = panelName === sidepanelName ? null : sidepanelName;
    setPanelName(nextPanelName);
    if (nextPanelName) refreshNotifications();
  };

  const countMentioned = mentioned ? mentioned.unreadedChats : 0;
  const arrayTypes: NotificationEntityTypes[] = [
    NotificationEntityTypes.NEW_PARTNER_REGISTERED,
    NotificationEntityTypes.CONTACT_NEXT_CYCLE,
    NotificationEntityTypes.CREDIT_CONTACT_NEXT_CYCLE,
  ];
  const isNextCycle = [
    NotificationEntityTypes.CONTACT_NEXT_CYCLE,
    NotificationEntityTypes.CREDIT_CONTACT_NEXT_CYCLE,
  ];

  const notificationsList
    = Object.entries(notificationsMap || {}) as [NotificationEntityTypes, AdminNotification[]][];

  let unreadedNotificationsCount = 0;
  notificationsList.forEach(([tp, items]) => {
    if (isNextCycle.includes(tp)) {
      items.forEach(item => {
        unreadedNotificationsCount += !item.isRead ? 1 : 0;
      });
    } else {
      const hasUnread = items.some(item => !item.isRead);
      if (hasUnread) {
        unreadedNotificationsCount += 1;
      }
    }
  });

  const countTotal = countMentioned + unreadedNotificationsCount;

  const count = useMemo(() => {
    return countTotal > 9 ? '9+' : countTotal;
  }, [countTotal]);

  const chats = useMemo(() => {
    return mentioned ? Object.values(mentioned?.chats || {}) : [];
  }, [mentioned]);

  const isEmpty = notificationsList.length === 0 && chats.length === 0;

  const clearNotifications = async () => {
    try {
      await adminHttp.clearNotifications();
      await refreshNotifications();
    } catch (err: any) {
      console.error(err);
    }
  };

  return (
    <>
      <div className={cls.icon} onClick={togglePanel}>
        <NotificationsIcon style={{ fill: '#fff' }} />
        {countTotal > 0 && <div className={cls.counter}>{count}</div>}
      </div>

      <Sidepanel
        name={sidepanelName}
        title="Notifications"
        action={notificationsList.length > 0 ? {
          title: 'Delete all',
          onClick: clearNotifications,
        } : undefined}
      >
        {isEmpty && <div className={cls.empty}>You have no notifications</div>}

        {notificationsList.map(([entity, items], index) => {
          if (arrayTypes.includes(entity)) {
            return items.map((item, itemIndex) => (
              <NotificationsItem
                key={`${index}-${itemIndex}`}
                entity={entity}
                items={[item]}
                refreshNotifications={refreshNotifications}
              />
            ));
          }

          return (
            <NotificationsItem
              key={index}
              entity={entity}
              items={items}
              refreshNotifications={refreshNotifications}
            />
          );
        })}

        {chats.length > 0 && (
          <div>
            <Divider />
            <ChatsList chats={chats} isMentions />
          </div>
        )}
      </Sidepanel>
    </>
  );
};
